import { toNumber } from 'lodash';
import { ICompanyAddress } from 'types/company';
import { IAddEditEmployee, IEmployeeDependants } from 'types/employee';
import IDependantsRequest from 'types/employee/IDependantsRequest';
import IEmployeeRequest from 'types/employee/IEmployeeRequest';
import IOnboardingLoginResponse from 'types/employee/IOnboardingLoginResponse';
import logError from 'utils/log-error';
import { shouldSendMfaOverride } from 'utils/mfa-override';

const formatLocalDateTime = (date: Date | string | null | undefined): string => {
  if (!date) return '';

  const d = typeof date === 'string' ? new Date(date) : date;

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0'); // Months are 0-based
  const day = String(d.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}T00:00:00`;
};

export const fromDependantsModelToDependantsRequest = (dependants: IEmployeeDependants): IDependantsRequest => {
  const employeeDependants: IDependantsRequest = {
    id: dependants.id !== undefined ? dependants.id : 0,
    partner: dependants.partner !== null ? dependants.partner : false,
    disabledPartner: dependants.disabledPartner !== null ? dependants.disabledPartner : false,
    children: dependants.children === '' || dependants.children === null ? 0 : toNumber(dependants.children),
    disabledChildren:
      dependants.disabledChildren === '' || dependants.disabledChildren === null
        ? 0
        : toNumber(dependants.disabledChildren),
    elderly: dependants.elderly === '' || dependants.elderly === null ? 0 : toNumber(dependants.elderly),
  };
  return employeeDependants;
};

export const getIdDateString = (employee: IAddEditEmployee, isStartDate: boolean): string => {
  const idDate = isStartDate ? employee.idPeriodStart : employee.idPeriodEnd;
  if (employee.permanent && idDate.toString().length === 0) {
    return '1/1/0001';
  }

  if (isStartDate) {
    return formatLocalDateTime(employee.idPeriodStart);
  }
  return formatLocalDateTime(employee.idPeriodEnd);
};

export const fromEmployeeModelToEmployeeRequest = (employee: IAddEditEmployee): IEmployeeRequest => {
  let defaultStartDate = '';

  if (employee?.startDate && typeof employee?.startDate === 'string' && employee.permanent) {
    defaultStartDate = formatLocalDateTime(new Date(employee.startDate));
  }

  const employeeRequest: IEmployeeRequest = {
    id: employee.id,
    firstName: employee.firstName,
    lastName: employee.lastName,
    dateOfBirth:
      typeof employee.dateOfBirth === 'string' ? employee.dateOfBirth : formatLocalDateTime(employee.dateOfBirth),
    birthplace: employee.birthplace,
    birthCountry: toNumber(employee.birthCountry),
    nationality: toNumber(employee.nationality),
    inss: employee.inss,
    languageId: toNumber(employee.languageId),
    gender: toNumber(employee.gender),
    workerclass: toNumber(employee.workerclass),
    reasonForEmployment: toNumber(employee.reasonForEmployment),
    iban: employee.iban,
    bic: employee.bic,
    idCardNumber: employee.idCardNumber,
    modeOfTransport: toNumber(employee.modeOfTransport),
    maritalStatus: toNumber(employee.maritalStatus),
    dependants: employee.hasDependants ? fromDependantsModelToDependantsRequest(employee.dependants) : null,
    mobileNumber: employee.mobileNumber,
    emailAddress: employee.emailAddress,
    iceMobileNumber: employee.iceMobileNumber,
    idUpload: employee.idUpload,
    idUploadURL: employee.idUploadURL,
    idUploadBack: employee.idUploadBack,
    idUploadBackURL: employee.idUploadBackURL,
    idPeriodStart: getIdDateString(employee, true),
    idPeriodEnd: getIdDateString(employee, false),
    agreeGdpr: employee.agreeGdpr,
    agreePictureAgreement: employee.agreePictureAgreement,
    letterOfIntentCheck: employee.letterOfIntentCheck,
    pictureUpload: employee.pictureUpload,
    pictureUploadURL: employee.pictureUploadURL,
    password: employee.password,
    address: employee.address,
    stayAddress: employee.stayAddress?.countryId ? employee.stayAddress : null,
    hasStayAddress: employee.hasStayAddress,
    yearsOfExperience: toNumber(employee.yearsOfExperience),
    status: toNumber(employee.status),
    permanent: employee.permanent,
    startDate:
      typeof employee.startDate !== 'string' && employee.startDate && employee.permanent
        ? formatLocalDateTime(employee.startDate)
        : defaultStartDate,
    companyLocationIds: employee.companyLocationIds.map(e => {
      const val = toNumber(e?.value);
      if (Number.isNaN(val)) {
        return e;
      }
      return val;
    }),
    retired: employee.retired,
    terminated: employee.terminated,
    eligible: employee.eligible,
  };

  if (employee.retired && employee.retiredDate) {
    if (typeof employee.retiredDate !== 'string') {
      employeeRequest.retiredDate = employee.retiredDate.toDateString();
    } else {
      employeeRequest.retiredDate = employee.retiredDate;
    }
  }

  if (employee.terminated && employee.terminatedDate) {
    if (typeof employee.terminatedDate !== 'string') {
      employeeRequest.terminatedDate = employee.terminatedDate.toDateString();
    } else {
      employeeRequest.terminatedDate = employee.terminatedDate;
    }
  }

  // Only ever added as a real boolean. Both employee endpoints are multipart/form-data built by
  // appending every key of this object, so a null would go over the wire as the string "null" and
  // fail model validation for a nullable boolean - and only a SuperAdmin may write it at all, so for
  // every other caller the key must simply not be here. See shouldSendMfaOverride.
  if (shouldSendMfaOverride(employee.mfaRequiredOverride)) {
    employeeRequest.mfaRequiredOverride = employee.mfaRequiredOverride;
  }

  return employeeRequest;
};

export const fromEmployeeRequestToEmployeeModel = (employee: IAddEditEmployee): IAddEditEmployee => {
  const defaultDependants: IEmployeeDependants = {
    id: 0,
    partner: false,
    disabledPartner: false,
    children: '',
    disabledChildren: '',
    elderly: '',
  };

  const defaultStayAddress: ICompanyAddress = {
    id: 0,
    streetName: '',
    streetNumber: '',
    busNumber: '',
    city: '',
    postalCode: '',
    countryId: '',
  };

  const employeeRequest: IAddEditEmployee = {
    ...employee,
    companyLocationIds: employee.companyLocationIds ? employee.companyLocationIds.map(String) : [],
    dependants: employee.hasDependants ? employee.dependants : defaultDependants,
    stayAddress: employee.hasStayAddress ? employee.stayAddress : defaultStayAddress,
  };
  return employeeRequest;
};

export const fromRequestResponseToOnboardingLoginResponse = (data: any): IOnboardingLoginResponse => {
  let onboardingLoginResponse: IOnboardingLoginResponse | null = null;
  try {
    const employeeId = toNumber(data.employeeId);
    const companyId = toNumber(data.companyId);
    const token = String(data.token);
    onboardingLoginResponse = {
      employeeId,
      companyId,
      token,
    };
  } catch {
    logError('Error parsing onboarding login response');
  }
  return onboardingLoginResponse;
};
